import { Brain, TrendingUp, TrendingDown, Minus, Shield, Clock, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const SENTIMENT_MAP = {
  BULLISH: { icon: TrendingUp, color: "text-green-600", bg: "bg-green-50", label: "Bullish" },
  BEARISH: { icon: TrendingDown, color: "text-red-600", bg: "bg-red-50", label: "Bearish" },
  NEUTRAL: { icon: Minus, color: "text-gray-600", bg: "bg-gray-50", label: "Neutral" },
};

const BIAS_LABELS = {
  LONG_BIAS: "Long Bias",
  SHORT_BIAS: "Short Bias",
  WAIT: "Wait & Watch",
  HEDGE: "Hedge",
};

export function AdvisoryCard({ advisory, loading, error, onRetry, onClose, className = "" }) {
  if (loading) {
    return (
      <Card className={`p-4 border border-blue-200 bg-blue-50/50 ${className}`} data-testid="advisory-loading">
        <div className="flex items-center gap-2 text-blue-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">Generating advisory...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`p-4 border border-red-200 bg-red-50/50 ${className}`} data-testid="advisory-error">
        <p className="text-sm text-red-600 mb-2">{error}</p>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} data-testid="advisory-retry-btn">
            Retry
          </Button>
        )}
      </Card>
    );
  }

  if (!advisory) return null;

  const sentiment = SENTIMENT_MAP[advisory.sentiment] || SENTIMENT_MAP.NEUTRAL;
  const SentimentIcon = sentiment.icon;

  return (
    <Card className={`p-4 border border-gray-200 space-y-3 ${className}`} data-testid="advisory-card">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full ${sentiment.bg} flex items-center justify-center`}>
            <SentimentIcon className={`w-4 h-4 ${sentiment.color}`} />
          </div>
          <div>
            <span className={`text-xs font-bold ${sentiment.color}`}>{sentiment.label}</span>
            <span className="text-[10px] text-gray-400 ml-1.5">
              {advisory.confidence || "MEDIUM"} confidence
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
            {BIAS_LABELS[advisory.action_bias] || advisory.action_bias}
          </span>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xs">&times;</button>
          )}
        </div>
      </div>

      {/* Thesis */}
      <p className="text-sm text-gray-700 leading-relaxed" data-testid="advisory-thesis">
        {advisory.thesis}
      </p>

      {/* Key Levels */}
      {advisory.key_levels && (advisory.key_levels.support?.length > 0 || advisory.key_levels.resistance?.length > 0) && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-green-50 rounded-lg p-2">
            <div className="text-green-600 font-semibold mb-1 flex items-center gap-1">
              <Shield className="w-3 h-3" /> Support
            </div>
            {(advisory.key_levels.support || []).map((lvl, i) => (
              <div key={i} className="text-green-700 font-mono">{typeof lvl === 'number' ? lvl.toLocaleString("en-IN") : lvl}</div>
            ))}
          </div>
          <div className="bg-red-50 rounded-lg p-2">
            <div className="text-red-600 font-semibold mb-1 flex items-center gap-1">
              <Shield className="w-3 h-3" /> Resistance
            </div>
            {(advisory.key_levels.resistance || []).map((lvl, i) => (
              <div key={i} className="text-red-700 font-mono">{typeof lvl === 'number' ? lvl.toLocaleString("en-IN") : lvl}</div>
            ))}
          </div>
        </div>
      )}

      {/* Risks */}
      {advisory.risks && advisory.risks.length > 0 && (
        <div className="text-xs space-y-1">
          <div className="font-semibold text-gray-500">Risks</div>
          {advisory.risks.map((risk, i) => (
            <div key={i} className="text-gray-600 flex gap-1.5">
              <span className="text-orange-400 mt-0.5">&#9679;</span>
              <span>{risk}</span>
            </div>
          ))}
        </div>
      )}

      {/* Note + Time Horizon */}
      <div className="flex items-center justify-between text-[10px] text-gray-400 pt-1 border-t border-gray-100">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {advisory.time_horizon?.replace("_", " ") || "SHORT TERM"}
        </span>
        <span className="flex items-center gap-1">
          <Brain className="w-3 h-3" /> AI Advisory
        </span>
      </div>
      {advisory.note && (
        <p className="text-[10px] text-gray-500 italic">{advisory.note}</p>
      )}
    </Card>
  );
}
